// WizardNavigation component matching the HTML design exactly
import React from "react";

interface WizardNavigationProps {
  currentStep: number;
  totalSteps: number;
  onCancel: () => void;
  onPrevious: () => void;
  onNext: () => void;
  onSubmit?: () => void;
  loading?: boolean;
  mode?: "create" | "edit";
}

const WizardNavigation: React.FC<WizardNavigationProps> = ({
  currentStep,
  totalSteps,
  onCancel,
  onPrevious,
  onNext,
  onSubmit,
  loading = false,
  mode = "create",
}) => {
  const isFirstStep = currentStep === 1;
  const isLastStep = currentStep === totalSteps;

  const nextButtonText = isLastStep
    ? mode === "create"
      ? "Create Product"
      : "Update Product"
    : "Next Step";

  const handleNext = () => {
    if (isLastStep && onSubmit) {
      onSubmit();
    } else {
      onNext();
    }
  };

  return (
    <footer className="tw-fixed sm:tw-sticky tw-bottom-0 tw-left-0 tw-right-0 sm:tw-left-auto sm:tw-right-auto tw-z-30 tw-bg-white dark:tw-bg-[#1C252E] tw-border-t sm:tw-border-y tw-border-gray-200 dark:tw-border-[#233648] tw-px-4 md:tw-px-6 tw-py-4 tw-shadow-lg sm:tw-shadow-none">
      {/* Mobile Layout (Vertical) */}
      <div className="tw-flex tw-flex-col tw-gap-3 sm:tw-hidden">
        <button
          onClick={handleNext}
          disabled={loading}
          className={`
            tw-flex tw-w-full tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-lg 
            tw-h-10 tw-bg-primary tw-px-4 tw-text-sm tw-font-bold tw-leading-normal tw-tracking-wide 
            tw-text-white hover:tw-bg-primary-dark tw-transition-colors
            ${loading ? "tw-opacity-50 tw-cursor-not-allowed" : ""}
          `}
        >
          {loading ? "Processing..." : nextButtonText}
        </button>
        <div className="tw-flex tw-items-center tw-gap-2">
          <button
            onClick={onCancel}
            className="tw-flex-1 tw-px-3 tw-py-2.5 tw-rounded-lg tw-border tw-border-gray-300 tw-text-sm tw-font-medium tw-transition-colors tw-text-gray-700 hover:tw-bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={onPrevious}
            disabled={isFirstStep || loading}
            className={`
              tw-flex-1 tw-px-3 tw-py-2.5 tw-rounded-lg tw-border tw-border-gray-300 tw-text-sm tw-font-medium tw-transition-colors
              ${
                isFirstStep || loading
                  ? "tw-text-gray-400 tw-cursor-not-allowed tw-bg-gray-50"
                  : "tw-text-gray-700 hover:tw-bg-gray-100"
              }
            `}
          >
            Previous Step
          </button>
        </div>
      </div>

      {/* Desktop Layout (Horizontal) */}
      <div className="tw-hidden sm:tw-flex tw-items-center tw-justify-between tw-max-w-4xl tw-mx-auto">
        <div className="tw-flex tw-items-center tw-gap-3">
          <button
            onClick={onCancel}
            className="tw-px-4 md:tw-px-6 tw-py-2.5 tw-rounded-lg tw-border tw-border-gray-300 tw-text-sm tw-font-semibold tw-transition-colors tw-text-gray-700 hover:tw-bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={onPrevious}
            disabled={isFirstStep || loading}
            className={`
              tw-px-4 md:tw-px-6 tw-py-2.5 tw-rounded-lg tw-border tw-border-gray-300 tw-text-sm tw-font-semibold tw-transition-colors
              ${
                isFirstStep || loading
                  ? "tw-text-gray-400 tw-cursor-not-allowed tw-bg-gray-50"
                  : "tw-text-gray-700 hover:tw-bg-gray-100"
              }
            `}
          >
            <span className="tw-flex tw-items-center tw-gap-2">
              <span className="material-symbols-outlined tw-text-base">
                chevron_left
              </span>
              Previous Step
            </span>
          </button>
        </div>
        <button
          onClick={handleNext}
          disabled={loading}
          className={`
            tw-flex tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-lg 
            tw-h-10 tw-bg-primary tw-px-6 tw-text-sm tw-font-bold tw-leading-normal tw-tracking-wide 
            tw-text-white hover:tw-bg-primary-dark tw-transition-colors tw-gap-2
            ${loading ? "tw-opacity-50 tw-cursor-not-allowed" : ""}
          `}
        >
          {loading ? (
            "Processing..."
          ) : (
            <>
              {nextButtonText}
              {!isLastStep && (
                <span className="material-symbols-outlined tw-text-base">
                  chevron_right
                </span>
              )}
            </>
          )}
        </button>
      </div>
    </footer>
  );
};

export default WizardNavigation;
